import { Component, computed, forwardRef, inject, input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { tap } from 'rxjs';
import {
  maxCircumferenceValidator,
  minCircumferenceValidator,
  minMaxCircumferenceValidator,
} from '../belt-circumference/belt-circumference-validators';
import { BeltCircumferenceComponent } from '../belt-circumference/belt-circumference.component';

type CircumferenceRange = { min: number; max: number };
type RangeType = 'predefined' | 'custom';
type BeltCircumferenceRangeData = {
  type: RangeType;
  min: number;
  max: number;
  confirmed: boolean;
};
type RangeForm = {
  range: CircumferenceRange | 'custom';
  customRange: CircumferenceRange;
  confirmed: boolean;
};

@Component({
  imports: [
    MatSelectModule,
    MatSlideToggleModule,
    MatInputModule,
    ReactiveFormsModule,
    MatIconModule,
    BeltCircumferenceComponent,
  ],
  selector: 'vectronic-formly-belt-circumference-range',
  template: ` <form [formGroup]="rangeControl" class="range-form">
    <mat-form-field class="full-width">
      <mat-label>{{ label() }}</mat-label>
      <mat-select
        [id]="!isCustom ? id() : undefined"
        formControlName="range"
        [compareWith]="compareFn"
        (selectionChange)="onSelectionChange($event.value)"
      >
        @for (option of options; track option) {
          <mat-option [value]="option.value">{{ option.label }}</mat-option>
        }
      </mat-select>
    </mat-form-field>

    @if (isCustom) {
      @if (adminView()) {
        <div class="custom-range-card admin" [class.confirmed]="isConfirmed">
          <header class="status-header">
            @if (isConfirmed) {
              <mat-icon>check_circle</mat-icon>
            } @else {
              <mat-icon>error_outline</mat-icon>
            }
            <span>Custom belt circumference range</span>
            <mat-slide-toggle formControlName="confirmed"> Confirmed </mat-slide-toggle>
          </header>

          <div class="custom-range-inputs">
            <mat-form-field appearance="outline">
              <vectronic-formly-belt-circumference
                [id]="id()"
                [min]="1"
                [maxRatio]="maxRatio"
                [formControl]="customRangeControl"
              ></vectronic-formly-belt-circumference>
              <span matTextSuffix>cm</span>
              <mat-error>{{ customRangeError() }} </mat-error>
            </mat-form-field>
          </div>
        </div>
      } @else {
        <div class="custom-range-card">
          <header class="status-header">
            <span>Custom belt circumference range</span>
          </header>

          <div class="custom-range-inputs">
            <mat-form-field appearance="outline">
              <vectronic-formly-belt-circumference
                [id]="id()"
                [min]="1"
                [maxRatio]="maxRatio"
                [formControl]="customRangeControl"
              ></vectronic-formly-belt-circumference>
              <span matTextSuffix>cm</span>
              <mat-error>{{ customRangeError() }} </mat-error>
            </mat-form-field>
          </div>
        </div>
      }
    }
  </form>`,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BeltCircumferenceRangeComponent),
      multi: true,
    },
  ],
  styles: `
    .range-form {
      display: flex;
      flex-direction: column;
    }

    .full-width {
      width: 100%;
    }

    .custom-range-card {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
      &.admin {
        --mat-form-field-filled-container-color: none;
        padding: 1rem 1.2rem;
        border-radius: 1rem;
        border: 1px solid rgba(0, 0, 0, 0.12);

        box-shadow: none;
        background-color: #f8d7da;
        border-color: #bf3443;
        margin-bottom: 1.25rem;

        &.confirmed {
          background-color: #d4edda;
          border-color: #114b1e;
          .status-header {
            color: #155724;
          }
        }

        header {
          color: #721c24;
        }
      }
      header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-weight: 500;

        span {
          font-size: 1rem;
        }
      }

      .custom-range-inputs {
        display: grid;
      }
    }
    span[mattextsuffix] {
      margin-left: 0.5rem;
    }
  `,
})
export class BeltCircumferenceRangeComponent implements ControlValueAccessor, OnInit {
  fb = inject(FormBuilder);

  ranges = input.required<CircumferenceRange[]>();
  label = input<string>('Circumference range');
  required = input<boolean>(false);
  adminView = input<boolean>(false);
  id = input<string>('');
  maxRatio = Infinity;

  rangeControl = this.fb.group({
    range: this.fb.control<CircumferenceRange | 'custom' | null>(null),
    customRange: this.fb.control<CircumferenceRange>(null),
    confirmed: this.fb.nonNullable.control(false),
  });
  options: { value: RangeForm['range']; label: string }[] = [];
  onChange: (value: BeltCircumferenceRangeData) => void;
  onTouched: () => void;
  customRange = toSignal(this.rangeControl.get('customRange').valueChanges);
  maxFrom = computed(() => (this.customRange().max ?? Infinity) - 1);
  minTo = computed(() => (this.customRange().min ?? 0) + 1);
  touched = signal<boolean>(false);
  disabled = signal<boolean>(false);
  customRangeError = computed(() => {
    this.customRange();
    if (this.customRangeControl.status !== 'INVALID') return null;
    if (this.customRangeControl.getError('minMaxCircumference')) {
      return this.customRangeControl.getError('minMaxCircumference').message;
    }
    if (this.customRangeControl.getError('minCircumference')) {
      return this.customRangeControl.getError('minCircumference').message;
    }
    if (this.customRangeControl.getError('maxCircumference')) {
      return this.customRangeControl.getError('maxCircumference').message;
    }
    if (this.customRangeControl.getError('customRangeRequired') != null) {
      return this.customRangeControl.getError('customRangeRequired').message;
    }
  });

  constructor() {
    this.rangeControl.valueChanges.pipe(takeUntilDestroyed()).subscribe((rangeData) => {
      const currentState = { ...this.rangeControl.getRawValue(), ...rangeData };
      const range = currentState.range;
      const customRange = currentState.customRange;
      const isRangeCustom = typeof range === 'string';
      const type = isRangeCustom ? 'custom' : 'predefined';
      const min = isRangeCustom ? customRange?.min : range?.min;
      const max = isRangeCustom ? customRange?.max : range?.max;
      const confirmed = isRangeCustom ? currentState.confirmed : true;
      this.onChange?.({ type, min, max, confirmed });
    });
    this.rangeControl
      .get('customRange')
      .valueChanges.pipe(
        takeUntilDestroyed(),
        tap(() => {
          if (this.touched() && !this.disabled()) {
            this.rangeControl.patchValue({ confirmed: false });
          }
        }),
      )
      .subscribe();
  }

  get customRangeControl() {
    return this.rangeControl.get('customRange') as FormControl<CircumferenceRange>;
  }
  get customRangeConfirmedControl() {
    return this.rangeControl.get('confirmed') as FormControl<boolean>;
  }
  ngOnInit(): void {
    this.options = [
      ...this.ranges().map((range) => ({
        value: { min: range.min, max: range.max },
        label: `${range.min}-${range.max} cm`,
      })),
      { value: 'custom', label: 'Special Request' },
    ];
    if (this.required()) {
      this.rangeControl.get('range').addValidators(Validators.required);
    }
    this.customRangeControl.addValidators([
      minCircumferenceValidator(() => 1),
      minMaxCircumferenceValidator,
      maxCircumferenceValidator(() => 1),
      this.customRangeRequiredValidator,
    ]);
    this.customRangeConfirmedControl.addValidators(this.customRangeConfirmedValidator);
  }
  get isCustom() {
    return this.rangeControl.get('range')?.value === 'custom';
  }
  get isConfirmed() {
    return this.rangeControl.get('confirmed')?.value;
  }
  writeValue(value: BeltCircumferenceRangeData): void {
    let formControlValue: RangeForm;
    if (!value) {
      formControlValue = { range: null, customRange: null, confirmed: null };
    } else if (value.type === 'predefined') {
      formControlValue = {
        range: { min: value.min, max: value.max },
        customRange: null,
        confirmed: true,
      };
    } else if (value.type === 'custom') {
      formControlValue = {
        range: 'custom',
        customRange: { min: value.min ?? null, max: value.max ?? null },
        confirmed: value.confirmed,
      };
    }
    this.touched.set(false);
    this.rangeControl.setValue(formControlValue, { emitEvent: false });
    this.rangeControl.get('customRange').updateValueAndValidity({ onlySelf: true });
    this.rangeControl.markAsPristine();
    this.rangeControl.markAsUntouched();
  }
  registerOnChange(fn: (value: BeltCircumferenceRangeData) => void): void {
    this.onChange = (value) => {
      fn(value);
      this.touched.set(true);
    };
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = () => {
      fn();
      this.touched.set(true);
    };
  }
  setDisabledState?(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
    isDisabled ? this.rangeControl.disable() : this.rangeControl.enable();
  }

  compareFn(option: CircumferenceRange, selection: CircumferenceRange) {
    if (selection == null) return false;
    return option.min === selection.min && option.max === selection.max;
  }
  onSelectionChange(value: RangeForm['range']) {
    if (value === 'custom') {
      this.rangeControl.patchValue({ confirmed: false, customRange: null });
    }
  }
  private customRangeRequiredValidator: ValidatorFn = (
    control: AbstractControl,
  ): ValidationErrors | null => {
    const isCustom = control.parent.get('range').getRawValue() === 'custom';
    const customRangeValue = control.getRawValue();
    if (!isCustom) return null;
    if (!(customRangeValue?.min != null && customRangeValue?.max != null)) {
      return { customRangeRequired: { message: 'Custom circumference is missing' } };
    }
    return null;
  };
  private customRangeConfirmedValidator: ValidatorFn = (
    control: AbstractControl,
  ): ValidationErrors | null => {
    const isCustom = control.parent.get('range').getRawValue() === 'custom';
    const isAdmin = this.adminView();
    if (!isCustom || !isAdmin) return null;
    if (!control.getRawValue()) {
      return { customRangeNotConfirmed: { message: 'Custom circumference not confirmed' } };
    }
    return null;
  };
}
